import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { map, Observable } from 'rxjs';

import { EnvironmentService, ErableHttpService, IdUtils } from '@ecm/consumer';
import { EcmDispatcherModule } from '@ecm/dispatcher';

import { Contract } from '../business/api/contracts/contract/contract';
import { Usage } from '../business/api/contracts/contract/usage/usage';
import { ContractsBusinessModel } from '../business/api/contracts/contracts.business-model';
import { ContractInContractSelector } from '../business/api/contractSelector/contract-in-contract-selector';
import { ContractSelector } from '../business/api/contractSelector/contract-selector';
import { CarrouselItem } from '../business/api/portfolio/carrouselItem';
import { Family } from '../business/api/portfolio/family';
import { Portfolio } from '../business/api/portfolio/portfolio';
import { Service } from '../business/api/portfolio/service';
import { mapResponseToPortfolio } from '../mapper/portfolio.mapper';
import { PortfolioHelper } from '../utils/portfolio-helper';
import { UsageHelper } from '../utils/usage-helper';
import { ContractSelectorService } from './contract-selector.service';

@Injectable({
    providedIn: 'root',
})
export class PortfolioService {
    /**
     * Page ID property for BCO log
     * @type {string}
     */
    private readonly pageId: string;
    /**
     * Functionality property for BCO log
     * @type {string}
     */
    private readonly functionality: string = 'CSELECTOR';

    // eslint-disable-next-line deprecation/deprecation -- Instance should be removed when method are no more used by FT
    private csService: ContractSelectorService;

    private defaultPortfolioData = null;

    public constructor(
        private environmentService: EnvironmentService,
        private httpService: ErableHttpService,
        private cookieService: CookieService,
    ) {
        this.pageId = IdUtils.generateRandomId();
        // csService is instantiated here to avoid declaring it as a global Angular Service (deprecated)
        // eslint-disable-next-line deprecation/deprecation -- should be removed when methods are no more used
        this.csService = new ContractSelectorService(environmentService, httpService, this.pageId);
    }

    /**
     * Return full response from the GET /portfolio API
     * @param {string} [filter] security/telco filter param
     * @param {boolean} [force] force call to service if true
     */
    public getPortfolio(filter?: string, force?: boolean): Observable<Portfolio> {
        const env = this.environmentService.instant();
        const url = env.API.PORTFOLIO_URL;
        if (filter) {
            return this.callPortfolioData(url.replace(/current\?filter=([^&]+)/, 'current?filter=' + filter), force);
        }
        if (this.defaultPortfolioData === null) {
            this.defaultPortfolioData = this.callPortfolioData(url, force);
        }
        return this.defaultPortfolioData;
    }

    public getRawContracts(): Observable<ContractsBusinessModel>;
    public getRawContracts(filter: string): Observable<ContractsBusinessModel>;
    /**
     * Returns the contracts collection from /portfolio API
     * @param {string} [filter] security/telco filter param sent to API
     * @returns {Observable<ContractsBusinessModel>}
     */
    public getRawContracts(filter?: string): Observable<ContractsBusinessModel> {
        return this.getPortfolio(filter).pipe(map((response: any) => ({ contracts: response.contracts })));
    }

    /**
     * Returns the first family of the families collection from /portfolio API
     */
    public getFamily(): Observable<Family> {
        return this.getPortfolio().pipe(map((response: Portfolio) => response.families?.[0]));
    }

    /**
     * Returns the carrousel collection from /portfolio API
     */
    public getCarrouselItems(): Observable<Array<CarrouselItem>> {
        return this.getPortfolio().pipe(map((response: Portfolio) => response.carrousel || []));
    }

    /**
     * Returns the services collection from /portfolio API
     */
    public getServices(): Observable<Array<Service>> {
        return this.getPortfolio().pipe(map((response: Portfolio) => response.services || []));
    }

    /**
     * Returns the contract matching given cid
     * @param {string} cid Contract ID to find
     * @param {string} [filters] security/telco filter param
     * @returns {Observable<Contract>}
     */
    public getContractByCid(cid: string, filters?: string): Observable<Contract> {
        return this.getRawContracts(filters).pipe(
            map((contractsBusinessModel: ContractsBusinessModel) => {
                const result: Contract = contractsBusinessModel.contracts.find((contract: Contract) => {
                    return cid === contract.cid;
                });
                return result !== undefined ? result : null;
            }),
        );
    }

    /**
     * Returns logged (default) contract
     * @returns {Observable<Contract|undefined>}
     */
    public getLoggedContract(): Observable<Contract | undefined> {
        return this.getRawContracts().pipe(
            map((contractsBusinessModel: ContractsBusinessModel) =>
                PortfolioHelper.getLoggedContract(contractsBusinessModel.contracts),
            ),
        );
    }

    /**
     * Returns only Telco contracts
     * @returns {Observable<Contract[]>}
     */
    public getTelcoContracts(): Observable<Contract[]> {
        return this.getRawContracts().pipe(
            map((contractsBusinessModel: ContractsBusinessModel) => {
                return contractsBusinessModel.contracts.filter((contract: Contract) => {
                    return (
                        Object.prototype.hasOwnProperty.call(contract, 'telco') &&
                        !Object.prototype.hasOwnProperty.call(contract, 'security')
                    );
                });
            }),
        );
    }

    /**
     * Returns only Security contracts
     * @returns {Observable<Contract[]>}
     */
    public getSecurityContracts(): Observable<Contract[]> {
        return this.getRawContracts().pipe(
            map((contractsBusinessModel: ContractsBusinessModel) => {
                return contractsBusinessModel.contracts.filter((contract: Contract) => {
                    return (
                        Object.prototype.hasOwnProperty.call(contract, 'security') &&
                        !Object.prototype.hasOwnProperty.call(contract, 'telco')
                    );
                });
            }),
        );
    }

    /**
     * Get usages for specified contract id
     * @param {string} contractId
     * @returns {Observable<Usage[]>}
     */
    public getUsagesFromContractCid(contractId: string): Observable<Usage[]> {
        return this.getContractByCid(contractId).pipe(map((contract: Contract) => contract?.usages || []));
    }

    /**
     * Get contracts having specified usages
     * @param {string | string[]} authorizedUsage
     * @param {string} [filter]
     * @returns {Observable<Contract[]>}
     */
    public getContractsHavingUsage(authorizedUsage: string | string[], filter?: string): Observable<Contract[]> {
        return this.getRawContracts(filter).pipe(
            map((contractsBusinessModel: ContractsBusinessModel) => {
                authorizedUsage = typeof authorizedUsage === 'string' ? [authorizedUsage] : authorizedUsage;

                return contractsBusinessModel.contracts.filter((contract: Contract) => {
                    contract.usages =
                        contract?.usages?.filter((usage: Usage) =>
                            authorizedUsage.includes(UsageHelper.extractUsageCodeFromUsage(usage.usageCode)),
                        ) || [];

                    // fired contract with usages length = 0
                    return contract.usages.length > 0;
                });
            }),
        );
    }

    /**
     * Set cookie for contract - 2h duration - if the info is given by PortfolioService.
     * @param {Contract} contract
     */
    public setContractCookie(contract: Contract): void {
        if (contract.telco && contract.telco.cookieContract) {
            const expireDate = new Date();
            expireDate.setHours(expireDate.getHours() + 2);
            this.cookieService.set('contrat', contract.telco.cookieContract, expireDate, '/', '.orange.fr', true);
            EcmDispatcherModule.sendConsoleInfo(
                PortfolioService.name,
                '[Contract Cookie] ' + contract.telco.cookieContract + ' has been written',
            );
        }
    }

    /**
     * Check if the contract is metropolitan
     *  - if the contract has no geographicalArea, it is considered as metropolitan
     * @param {string} contractId
     * @returns {Observable<boolean>}
     */
    public isContractMetropolitan(contractId: string): Observable<boolean> {
        return this.getContractByCid(contractId).pipe(
            map(
                (contract: Contract) =>
                    !contract?.contractInstallationArea?.geographicalArea ||
                    contract?.contractInstallationArea?.geographicalArea === 'METRO',
            ),
        );
    }

    /**
     * Check if the contract is réunion
     * @param {string} contractId
     * @returns {Observable<boolean>}
     */
    public isContractReunion(contractId: string): Observable<boolean> {
        return this.getContractByCid(contractId).pipe(
            map(
                (contract: Contract) =>
                    contract?.contractInstallationArea?.geographicalArea === 'ORM' &&
                    contract?.contractInstallationArea?.departmentLabel === 'REUNION',
            ),
        );
    }

    /** @deprecated Use getRawContracts() instead */
    public getContractSelector(): Observable<ContractSelector> {
        return this.csService.getFullResponse();
    }

    /** @deprecated Use getLoggedContract() instead */
    public getDefaultContract(): Observable<ContractInContractSelector> {
        return this.csService.getDefaultContract();
    }

    /** @deprecated Use getRawContracts() instead */
    public getContracts(): Observable<Map<string, ContractInContractSelector>> {
        return this.csService.getContracts();
    }

    /** @deprecated use getContractByCid() instead */
    public getContract(contractId: string): Observable<ContractInContractSelector> {
        return this.csService.getContract(contractId);
    }

    private callPortfolioData(url: string, force: boolean): Observable<Portfolio> {
        return this.httpService
            .get(url, {
                headers: new HttpHeaders().set('Accept', 'application/json;version=1'),
                feature: 'navigation',
                pageId: this.pageId,
                functionality: this.functionality,
                partner: 'ZGP',
                service: 'portfolio',
                force: force,
            })
            .pipe(map(mapResponseToPortfolio));
    }
}
